/**
 * Draft storage — the browser-side persistence of the working corridor.
 *
 * Pure module (no React) so the migration from the legacy single-plan draft to the corridor
 * draft can be tested directly. Keys, legacy detection and normalisation, save/load/clear,
 * and the last-opened plan id. Extracted from PlanContext.tsx on 261002 (register batch task).
 */

import type { Corridor, Plan } from '../types/plan';
import { planToCorridor } from '../types/plan';
import { defaultPhaseDirection } from './utils';

// Keys

export const STORAGE_KEY_DRAFT = 'sigtdd_draft';
export const STORAGE_KEY_CORRIDOR = 'sigtdd_corridor_draft';
export const STORAGE_KEY_LAST_PLAN = 'sigtdd_last_plan_id';

/** Check if data is legacy Plan format */
export function isLegacyPlan(data: unknown): data is Plan {
  return (
    typeof data === 'object' &&
    data !== null &&
    'intersections' in data &&
    Array.isArray((data as Plan).intersections) &&
    (data as Plan).intersections.length > 0 &&
    'offset' in (data as Plan).intersections[0]
  );
}

export function saveDraftToStorage(corridor: Corridor): void {
  try {
    const data = JSON.stringify({
      ...corridor,
      _savedAt: new Date().toISOString(),
    });
    localStorage.setItem(STORAGE_KEY_CORRIDOR, data);
    // Remove old legacy key if present
    localStorage.removeItem(STORAGE_KEY_DRAFT);
  } catch (e) {
    console.error('Failed to save draft:', e);
  }
}

export function loadDraftFromStorage(): Corridor | null {
  try {
    // Try new corridor format first
    let data = localStorage.getItem(STORAGE_KEY_CORRIDOR);
    if (data) {
      const parsed = JSON.parse(data);
      const { _savedAt, ...corridor } = parsed;
      return corridor as Corridor;
    }

    // Fall back to legacy plan format
    data = localStorage.getItem(STORAGE_KEY_DRAFT);
    if (data) {
      const parsed = JSON.parse(data);
      const { _savedAt, ...plan } = parsed;

      if (isLegacyPlan(plan)) {
        // Convert legacy plan to corridor
        const normalizedPlan = normalizeLegacyPlan(plan);
        return planToCorridor(normalizedPlan);
      }
    }

    return null;
  } catch {
    return null;
  }
}

export function clearDraft(): void {
  localStorage.removeItem(STORAGE_KEY_CORRIDOR);
  localStorage.removeItem(STORAGE_KEY_DRAFT);
}

export function hasDraft(): boolean {
  return (
    localStorage.getItem(STORAGE_KEY_CORRIDOR) !== null ||
    localStorage.getItem(STORAGE_KEY_DRAFT) !== null
  );
}


/** Remember the last opened database plan; storage failures are ignored. */
export function setLastPlanId(id: number | string): void {
  try { localStorage.setItem(STORAGE_KEY_LAST_PLAN, String(id)); } catch { /* storage unavailable */ }
}

export function getLastPlanId(): string | null {
  try { return localStorage.getItem(STORAGE_KEY_LAST_PLAN); } catch { return null; }
}

/** Normalize a legacy Plan before converting to Corridor */
export function normalizeLegacyPlan(plan: Plan): Plan {
  let corridorName = plan.name;

  const needsCrossStreet = plan.intersections.some(int => !int.crossStreet);
  let intersections = plan.intersections;

  if (needsCrossStreet && intersections.length > 0) {
    const parts = intersections.map(int => {
      const ampIdx = (int.name || '').indexOf(' & ');
      return ampIdx >= 0
        ? { corridor: int.name.slice(0, ampIdx), cross: int.name.slice(ampIdx + 3) }
        : null;
    });

    const allHaveAmp = parts.every(p => p !== null);
    const commonCorridor = allHaveAmp
      ? parts.every(p => p!.corridor === parts[0]!.corridor) ? parts[0]!.corridor : null
      : null;

    if (commonCorridor) {
      corridorName = commonCorridor;
      intersections = intersections.map((int, i) => ({
        ...int,
        crossStreet: int.crossStreet || parts[i]!.cross,
      }));
    } else {
      intersections = intersections.map(int => ({
        ...int,
        crossStreet: int.crossStreet || int.name || '',
      }));
    }
  }

  // Backfill phase directions
  intersections = intersections.map(int => ({
    ...int,
    phases: int.phases.map((p, i) => ({
      ...p,
      direction: p.direction ?? defaultPhaseDirection(i),
    })),
  }));

  return { ...plan, name: corridorName, intersections };
}

